import { faqsPl } from "@data/warsztaty-produktowe-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mvpClusterLinks,
  mvpKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mvp-landing/shared";
import {
  Calendar,
  CheckCircle,
  ClipboardList,
  Code,
  FileText,
  Lightbulb,
  ListChecks,
  Map,
  PenTool,
  Puzzle,
  Rocket,
  Search,
  Target,
  Users,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Warsztaty produktowe - zakres, ryzyka i wycena przed budową MVP";

export const description =
  "Warsztaty produktowe przed budową MVP: user stories, zakres, ryzyka i materiały do wyceny aplikacji. Discovery przed developmentem — zanim zaczniesz płacić za budowę.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Warsztaty produktowe przed MVP",
    description,
    "Product discovery workshops",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Warsztaty",
  headerTail: "produktowe",
  description:
    "Domknij zakres MVP, ryzyka i brief do wyceny, zanim zaczniesz development. Warsztaty discovery dla osób startujących z pomysłem na aplikację, SaaS albo produkt IoT.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz przebieg",
    href: "#proces",
  },
  benefits: [
    { text: "Zakres MVP i kryteria sukcesu", Icon: CheckCircle },
    { text: "User stories pod wycenę", Icon: CheckCircle },
    { text: "Mniej przeróbek w sprincie", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo są warsztaty produktowe?",
    p: "Warsztaty mają sens dokładnie wtedy, gdy wycena „na brief” nie działa: niepełne wymagania, twardy budżet, brak Figmy albo mieszanka hardware i aplikacji. Czy to Twój etap?",
  },
  points: [
    {
      Icon: Lightbulb,
      title: "Masz pomysł, ale nie masz jeszcze zakresu MVP",
      description:
        "Wiesz, jaki problem rozwiązujesz, ale lista funkcji jest chaosem. Warsztaty pomagają uciąć zakres do walidacji, nie do listy życzeń.",
    },
    {
      Icon: FileText,
      title: "Dostałeś wyceny, które mocno się rozjeżdżają",
      description:
        "Wykonawcy wyceniają różne rzeczy, bo brief jest niepełny. Domknięte user stories i ryzyka dają porównywalne oferty.",
    },
    {
      Icon: Puzzle,
      title: "Nie wiesz, czy to appka, SaaS czy urządzenie",
      description:
        "Mapujemy hipotezę biznesową i wybieramy formę produktu pod budżet oraz czas do pierwszego testu z użytkownikami.",
    },
    {
      Icon: Users,
      title: "Chcesz UX/UI discovery przed developmentem",
      description:
        "Makiety low-fi, ścieżki użytkownika i priorytety — bez płacenia od razu za pełny design system i produkcyjny UI.",
    },
    {
      Icon: Target,
      title: "Budżet jest twardy i trzeba świadomie ciąć zakres",
      description:
        "Ustalamy, co zostaje w MVP, co idzie do backlogu i jakie ryzyka zostawiacie na później — transparentnie wobec inwestora lub zarządu.",
    },
  ],
  cta: {
    h4: "Brief jest niepełny?",
    text: "Umów konsultację — powiemy, czy wystarczy doprecyzowanie mailowe, czy warto zrobić warsztaty przed wyceną MVP.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Dlaczego warsztaty przed wyceną się opłacają?",
    p: "Na pierwszym kontakcie najczęściej pada to samo: „jak przygotować brief”, „potrzebuję wyceny”, „czy zrobimy NDA”. Warsztaty domykają lukę między pomysłem a ofertą, z którą da się sensownie ruszyć dalej.",
    imageAlt: "Warsztaty produktowe przed budową MVP",
  },
  values: [
    {
      Icon: ListChecks,
      title: "Materiały pod wycenę, nie tylko „spotkanie o pomyśle”",
      description:
        "User stories, kryteria sukcesu, ryzyka i propozycja stacku — baza do oferty ze stałą ceną albo planu Time & Material.",
    },
    {
      Icon: Map,
      title: "Świadome cięcie zakresu MVP",
      description:
        "Każda funkcja ma uzasadnienie walidacyjne. To chroni budżet lepiej niż „zróbmy wszystko w V1”.",
    },
    {
      Icon: Search,
      title: "Wykrywanie ryzyk wcześnie",
      description:
        "Integracje, BLE/hardware, AI, RODO, płatności — wychwytujemy kosztowne niespodzianki przed startem sprintów.",
    },
    {
      Icon: ClipboardList,
      title: "Lepszy brief dla Was i dla wykonawcy",
      description:
        "Nawet jeśli pójdziecie do innego software house’u — wychodzicie z dokumentacją, która podnosi jakość wycen.",
    },
    {
      Icon: Rocket,
      title: "Płynne przejście do budowy MVP",
      description:
        "Po warsztatach możemy od razu przejść do developmentu — zakres i wycena są już zsynchronizowane.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Co obejmują warsztaty produktowe",
    p: "Krótki discovery pod MVP — od mapy użytkowników po materiały do wyceny. Zakres sesji dopasowujemy do etapu produktu.",
  },
  services: [
    {
      Icon: Users,
      title: "Mapa użytkowników i problemów",
      description:
        "Kim jest użytkownik, jaki problem rozwiązujecie i jak wygląda sukces pierwszej wersji produktu.",
    },
    {
      Icon: ListChecks,
      title: "User stories i priorytety MVP",
      description:
        "Backlog podzielony na MVP / later — z kryteriami akceptacji gotowymi pod wycenę developmentu.",
    },
    {
      Icon: PenTool,
      title: "Makiety low-fi i kluczowe flow",
      description:
        "Ścieżki krytyczne (rejestracja, core value, płatność, admin) bez overdesignu.",
    },
    {
      Icon: Search,
      title: "Analiza ryzyk technicznych",
      description:
        "Integracje, AI, BLE/IoT, skala, bezpieczeństwo — co może wywrócić budżet lub timeline.",
    },
    {
      Icon: Puzzle,
      title: "Dobór formy produktu i stacku",
      description:
        "Web vs mobile vs PWA vs urządzenie — rekomendacja pod hipotezę, czas i budżet.",
    },
    {
      Icon: FileText,
      title: "Brief i materiały do wyceny",
      description:
        "Dokumentacja wyjściowa pod ofertę ze stałą ceną lub plan Time & Material.",
    },
    {
      Icon: Target,
      title: "Warsztaty pod twardy budżet",
      description:
        "Scenariusze zakresu A/B/C dopasowane do limitu budżetowego — świadome kompromisy.",
    },
    {
      Icon: ClipboardList,
      title: "Plan po warsztatach",
      description:
        "Następne kroki: wycena MVP, PoC techniczny albo dalsze UX — z jasnym właścicielem decyzji.",
    },
  ],
  cta: {
    h4: "Chcesz wycenę, która nie rozjedzie się o 3×?",
    p: "Zacznij od konsultacji — powiemy, czy warsztaty są potrzebne, czy brief wystarczy.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak wyglądają warsztaty produktowe",
    p: "Krótko, konkretnie, z jasnymi materiałami na wyjściu. Zdalnie lub hybrydowo — pod Twój kalendarz.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Konsultacja wstępna",
      duration: "30 minut",
      description:
        "Sprawdzamy etap produktu i czy warsztaty mają sens. Ustalamy cele sesji i uczestników.",
    },
    {
      Icon: FileText,
      title: "Materiały wejściowe",
      duration: "1–3 dni",
      description:
        "Brief, notatki, Figmy, konkurencja, ograniczenia budżetowe — zbieramy to, co już macie.",
    },
    {
      Icon: Users,
      title: "Sesje warsztatowe",
      duration: "1–3 dni",
      description:
        "Facylitacja: użytkownicy, zakres MVP, ryzyka, flow. Zwykle kilka sesji po 2–3 godziny.",
    },
    {
      Icon: PenTool,
      title: "Synteza i makiety",
      duration: "kilka dni",
      description:
        "Porządkujemy ustalenia, dociągamy low-fi i listę user stories z priorytetami.",
    },
    {
      Icon: ClipboardList,
      title: "Deliverables i przegląd",
      duration: "1 spotkanie",
      description:
        "Przekazujemy dokumentację, omawiamy decyzje i otwarte ryzyka.",
    },
    {
      Icon: Code,
      title: "Wycena MVP (opcjonalnie)",
      duration: "kilka dni",
      description:
        "Na bazie warsztatów przygotowujemy ofertę developmentu — stała cena lub Time & Material.",
    },
    {
      Icon: Rocket,
      title: "Start budowy lub handover",
      duration: "od razu",
      description:
        "Idziecie z nami w MVP albo zabieracie materiały do innego zespołu / in-house.",
    },
    {
      Icon: CheckCircle,
      title: "Follow-up po decyzji",
      duration: "w razie potrzeby",
      description:
        "Doprecyzowanie zakresu po feedbacku inwestora lub po porównaniu ofert.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Zamów warsztaty produktowe",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski zespół produktowy do discovery MVP",
  imageAlt: "Zespół Blues Brackets — warsztaty produktowe",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o warsztaty produktowe",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcesz wycenę opartą o realny zakres?",
    p: "Umów się na darmową konsultację — sprawdzimy, czy warsztaty produktowe są potrzebne przed budową MVP.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Warsztaty produktowe: zakres MVP, user stories, ryzyka i brief do wyceny. Polski software house — od pomysłu do oferty.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Nasze usługi" },
      { href: "#proces", text: "Proces współpracy" },
      { href: "#faq", text: "FAQ" },
      { href: "/mvp", text: "Budowa MVP" },
      ...mvpClusterLinks.filter(
        l => l.href !== "/mvp" && l.href !== "/warsztaty-produktowe",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mvpKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
